import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Sparkles,
  ChevronRight,
} from 'lucide-react';
import { Button, Card, Input } from '../components/design-system';
import { getTemplateById } from '../data/planTemplates';

export const PlanWizardPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [template, setTemplate] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [planTitle, setPlanTitle] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  useEffect(() => {
    const tmpl = getTemplateById(templateId);
    if (tmpl) {
      setTemplate(tmpl);
      setPlanTitle(t(tmpl.nameKey));
    } else {
      navigate('/plans/new');
    }
  }, [templateId, navigate, t]);

  if (!template) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const visibleQuestions = template.questions.filter(q => {
    if (!q.conditional) return true;
    return answers[q.conditional.field] === q.conditional.value;
  });

  const totalSteps = visibleQuestions.length + 1; // +1 for title step
  const isLastStep = currentStep === totalSteps - 1;
  const currentQuestion = currentStep > 0 ? visibleQuestions[currentStep - 1] : null;

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMultiSelect = (questionId, option) => {
    const current = answers[questionId] || [];
    const updated = current.includes(option)
      ? current.filter(o => o !== option)
      : [...current, option];
    setAnswers(prev => ({ ...prev, [questionId]: updated }));
  };

  const canProceed = () => {
    if (currentStep === 0) {
      return planTitle.trim().length > 0;
    }
    if (currentQuestion?.required) {
      const answer = answers[currentQuestion.id];
      if (currentQuestion.type === 'multiselect') {
        return answer && answer.length > 0;
      }
      return answer && answer.toString().trim().length > 0;
    }
    return true;
  };

  const handleNext = () => {
    if (isLastStep) {
      handleGeneratePlan();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else {
      navigate('/plans/new');
    }
  };

  const handleGeneratePlan = async () => {
    setIsGenerating(true);
    setGenerationProgress(0);

    try {
      // Create the plan
      const response = await fetch('/api/plans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
        body: JSON.stringify({
          template_id: template.id,
          title: planTitle,
          answers,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create plan');
      }

      const { plan } = await response.json();
      setGenerationProgress(30);

      // Generate AI content for each section
      const generateResponse = await fetch(`/api/plans/${plan.id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('omniapps_access_token')}`,
        },
      });

      if (generateResponse.ok) {
        setGenerationProgress(100);
        // Navigate to the plan editor
        setTimeout(() => {
          navigate(`/plans/${plan.id}`);
        }, 500);
      } else {
        // Even if generation fails, navigate to the plan
        navigate(`/plans/${plan.id}`);
      }
    } catch (error) {
      console.error('Failed to create plan:', error);
      setIsGenerating(false);
    }
  };

  const renderQuestionInput = () => {
    if (!currentQuestion) return null;

    const { id, type, options, questionKey } = currentQuestion;
    const value = answers[id] || (type === 'multiselect' ? [] : '');

    switch (type) {
      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handleAnswer(id, e.target.value)}
            placeholder={t('wizard.enterAnswer')}
            className="w-full"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleAnswer(id, e.target.value)}
            placeholder={t('wizard.enterAnswer')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[120px] resize-y"
          />
        );

      case 'select':
        return (
          <div className="grid gap-3 sm:grid-cols-2">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => handleAnswer(id, option)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  value === option
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className="font-medium">{t(`options.${option}`, option)}</span>
              </button>
            ))}
          </div>
        );

      case 'multiselect':
        return (
          <div className="grid gap-3 sm:grid-cols-2">
            {options.map((option) => {
              const isSelected = value.includes(option);
              return (
                <button
                  key={option}
                  onClick={() => handleMultiSelect(id, option)}
                  className={`p-4 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                  }`}>
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <span className="font-medium">{t(`options.${option}`, option)}</span>
                </button>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  if (isGenerating) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[500px]">
        <Card className="p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-primary-600 animate-pulse" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('wizard.generating')}
            </h2>
            <p className="text-gray-600">
              {t('wizard.generatingDescription')}
            </p>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${generationProgress}%` }}
            />
          </div>
          
          <p className="text-sm text-gray-500">
            {t('wizard.pleaseWait')}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>{t('wizard.step', { current: currentStep + 1, total: totalSteps })}</span>
            <span>{Math.round(((currentStep + 1) / totalSteps) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <Card className="p-8 mb-6">
          {currentStep === 0 ? (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t('wizard.nameYourPlan')}
              </h2>
              <p className="text-gray-600 mb-6">
                {t('wizard.nameYourPlanDescription')}
              </p>
              <Input
                value={planTitle}
                onChange={(e) => setPlanTitle(e.target.value)}
                placeholder={t('wizard.planTitlePlaceholder')}
                className="w-full text-lg"
              />
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t(currentQuestion.questionKey)}
              </h2>
              {currentQuestion.required && (
                <p className="text-sm text-gray-500 mb-6">
                  {t('wizard.required')}
                </p>
              )}
              {renderQuestionInput()}
            </>
          )}
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </Button>
          
          <Button onClick={handleNext} disabled={!canProceed()}>
            {isLastStep ? (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                {t('wizard.generatePlan')}
              </>
            ) : (
              <>
                {t('common.next')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        {/* Section Preview */}
        {currentStep === totalSteps - 1 && (
          <Card className="mt-8 p-6 bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-4">
              {t('wizard.sectionsToGenerate')}
            </h3>
            <div className="space-y-2">
              {template.sections.map((section, index) => (
                <div key={section.key} className="flex items-center gap-2 text-sm text-gray-600">
                  <ChevronRight className="h-4 w-4 text-primary-500" />
                  <span>{t(section.titleKey)}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
